import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';

interface ProductIdentifierSelectorProps {
  organisationId: string;
  currentValue: string;
  onUpdate: () => void;
  /** Read-only, e.g. while merchant Settings admin mode is off (FIG-345). */
  disabled?: boolean;
}

const IDENTIFIER_OPTIONS = ['SKU', 'ASIN', 'EAN', 'GTIN', 'UPC'];

export const ProductIdentifierSelector: React.FC<ProductIdentifierSelectorProps> = ({
  organisationId,
  currentValue,
  onUpdate,
  disabled = false,
}) => {
  const { toast } = useToast();

  const handleChange = async (value: string) => {
    const { error } = await supabase
      .from('organisations')
      .update({ product_identifier: value } as any)
      .eq('id', organisationId);

    if (error) {
      toast({ title: 'Failed to update product identifier', variant: 'destructive' });
      return;
    }

    toast({ title: 'Product identifier updated', description: `Set to ${value}` });
    onUpdate();
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Product Identifier</Label>
      <p className="text-sm text-muted-foreground">
        Choose the primary identifier used for products across reports, inventory, and purchase orders.
      </p>
      <Select value={currentValue} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {IDENTIFIER_OPTIONS.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
